/**
 * VerifyInstanceStatusDialog - Dialog para verificar status real de instância
 * 
 * Exibe resultado da verificação de status real do provider (WAHA, Z-API, etc)
 * comparando com o status local armazenado no banco.
 * 
 * Funcionalidades:
 * - Mostra loading durante verificação
 * - Exibe comparação: Status Local vs Status Real
 * - Mostra detalhes da sessão no provider (se existir)
 * - Alerta se houver divergência
 * - Opção de corrigir status se divergir
 * 
 * @version 1.0.0
 * @author Sistema
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Wifi, 
  WifiOff,
  RefreshCw,
  Loader2,
  ArrowRight,
  Smartphone,
  Server,
  Clock
} from 'lucide-react';
import { useVerifyInstanceStatus, VerifyStatusResult } from '@/hooks/useVerifyInstanceStatus';
import { ProviderInstance } from '@/hooks/useProviderInstances';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface VerifyInstanceStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: ProviderInstance | null;
}

/**
 * Mapeia status interno para configuração visual
 */
function getStatusConfig(status: string | undefined) {
  switch (status) {
    case 'active':
      return { 
        label: 'Conectado', 
        icon: CheckCircle2, 
        className: 'text-green-600 bg-green-50 border-green-200' 
      };
    case 'connecting':
      return { 
        label: 'Conectando', 
        icon: Wifi, 
        className: 'text-blue-600 bg-blue-50 border-blue-200' 
      };
    case 'disconnected':
      return { 
        label: 'Desconectado', 
        icon: WifiOff, 
        className: 'text-yellow-600 bg-yellow-50 border-yellow-200' 
      };
    case 'error':
      return { 
        label: 'Erro', 
        icon: XCircle, 
        className: 'text-red-600 bg-red-50 border-red-200' 
      };
    default:
      return { 
        label: status || 'Desconhecido', 
        icon: AlertTriangle, 
        className: 'text-muted-foreground bg-muted border-muted' 
      };
  }
}

/**
 * Mapeia status bruto do WAHA para texto legível
 */
function getWahaStatusLabel(wahaStatus: string | undefined, reason?: string) {
  if (reason === 'session_not_found') {
    return 'Sessão não encontrada';
  }
  
  switch (wahaStatus) {
    case 'WORKING':
      return 'Funcionando';
    case 'STOPPED':
      return 'Parado';
    case 'STARTING':
      return 'Iniciando';
    case 'SCAN_QR_CODE':
      return 'Aguardando QR Code';
    case 'FAILED':
      return 'Falhou';
    default:
      return wahaStatus || 'Desconhecido';
  }
}

export function VerifyInstanceStatusDialog({
  open,
  onOpenChange,
  instance
}: VerifyInstanceStatusDialogProps) {
  const [result, setResult] = useState<VerifyStatusResult | null>(null);
  const { verifyStatus, correctStatus, isVerifying, isCorrecting } = useVerifyInstanceStatus();

  // Executa verificação quando dialog abre
  useEffect(() => {
    if (open && instance?.inbox?.id) {
      setResult(null);
      verifyStatus.mutate(
        { 
          inboxId: instance.inbox.id, 
          localStatus: instance.inbox.status 
        },
        {
          onSuccess: (data) => setResult(data)
        }
      );
    }
  }, [open, instance?.inbox?.id]);

  // Handler para corrigir status
  const handleCorrectStatus = () => {
    if (!instance?.inbox?.id || !result?.data?.status) return;
    
    correctStatus.mutate({
      inboxId: instance.inbox.id,
      newStatus: result.data.status as 'active' | 'disconnected' | 'connecting' | 'error'
    }, {
      onSuccess: () => {
        onOpenChange(false);
      }
    });
  };

  // Handler para re-verificar
  const handleReVerify = () => {
    if (!instance?.inbox?.id) return;
    
    setResult(null);
    verifyStatus.mutate(
      { 
        inboxId: instance.inbox.id, 
        localStatus: instance.inbox.status 
      },
      {
        onSuccess: (data) => setResult(data)
      }
    );
  };

  const localConfig = getStatusConfig(instance?.inbox?.status);
  const realConfig = getStatusConfig(result?.data?.status);
  const LocalIcon = localConfig.icon;
  const RealIcon = realConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Verificar Status Real
          </DialogTitle>
          <DialogDescription>
            Verifica o status diretamente no servidor do provider
          </DialogDescription>
        </DialogHeader>

        {/* Informações da instância */}
        {instance && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Código:</span>
              <code className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                {instance.id.substring(0, 8).toUpperCase()}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Inbox:</span>
              <span>{instance.inbox?.name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Provider:</span>
              <Badge variant="outline" className="text-xs">
                {instance.provider?.display_name || instance.provider?.slug}
              </Badge>
            </div>
          </div>
        )}

        <Separator />

        {/* Loading state */}
        {isVerifying && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Verificando status no servidor {instance?.provider?.display_name}...
            </p>
          </div>
        )}

        {/* Resultado da verificação */}
        {result && !isVerifying && (
          <div className="space-y-4">
            {/* Comparação de status */}
            <div className="flex items-center justify-center gap-4">
              {/* Status Local */}
              <div className="flex flex-col items-center gap-2 p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground">Local</span>
                <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded border", localConfig.className)}>
                  <LocalIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">{localConfig.label}</span>
                </div>
              </div>

              <ArrowRight className="h-5 w-5 text-muted-foreground" />

              {/* Status Real */}
              <div className="flex flex-col items-center gap-2 p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground">Real</span>
                {result.success ? (
                  <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded border", realConfig.className)}>
                    <RealIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">{realConfig.label}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded border text-red-600 bg-red-50 border-red-200">
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Erro</span>
                  </div>
                )}
              </div>
            </div>

            {/* Alerta de divergência */}
            {result.mismatch && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Divergência detectada!</p>
                  <p className="text-amber-700">
                    O status local não corresponde ao status real do provider.
                  </p>
                </div>
              </div>
            )}

            {/* Erro */}
            {!result.success && result.error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
                <XCircle className="h-5 w-5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Erro na verificação</p>
                  <p className="text-red-700">{result.error}</p>
                </div>
              </div>
            )}

            {/* Detalhes da sessão no provider */}
            {result.success && result.data && (
              <div className="space-y-2 text-sm">
                <p className="font-medium text-muted-foreground">Detalhes do Provider:</p>
                
                <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-muted/50">
                  {result.data.wahaStatus && (
                    <div>
                      <span className="text-xs text-muted-foreground">Status WAHA:</span>
                      <p className="font-medium">
                        {getWahaStatusLabel(result.data.wahaStatus, result.data.reason)}
                      </p>
                    </div>
                  )}
                  
                  {result.data.reason && (
                    <div>
                      <span className="text-xs text-muted-foreground">Motivo:</span>
                      <p className="font-medium text-amber-600">{result.data.reason}</p>
                    </div>
                  )}
                  
                  {result.data.me?.pushName && (
                    <div className="flex items-center gap-1">
                      <Smartphone className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">WhatsApp:</span>
                      <p className="font-medium">{result.data.me.pushName}</p>
                    </div>
                  )}
                  
                  {result.data.engine && (
                    <div>
                      <span className="text-xs text-muted-foreground">Engine:</span>
                      <p className="font-medium">
                        {typeof result.data.engine === 'string' 
                          ? result.data.engine 
                          : 'GOWS' // Se for objeto, é engine GOWS
                        }
                      </p>
                    </div>
                  )}
                </div>
                
                {result.verifiedAt && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Verificado {formatDistanceToNow(new Date(result.verifiedAt), { 
                      addSuffix: true, 
                      locale: ptBR 
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {result && !isVerifying && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReVerify}
              disabled={isVerifying}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Verificar Novamente
            </Button>
          )}
          
          {result?.mismatch && result.data?.status && (
            <Button
              size="sm"
              onClick={handleCorrectStatus}
              disabled={isCorrecting}
            >
              {isCorrecting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Corrigir Status
            </Button>
          )}
          
          {!result?.mismatch && result?.success && (
            <Button
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              OK
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
